// components/Navigation.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';

const Nav = styled.nav`
  background-color: ${props => props.theme.colors.primary};
  padding: 0 2rem;
  height: 60px;
  position: relative;
  z-index: 100;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 102;

  @media (max-width: 768px) {
    display: block;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${props => props.isOpen ? '0' : '-100%'};
    height: 100vh;
    width: 250px;
    background-color: ${props => props.theme.colors.primary};
    flex-direction: column;
    padding: 80px 2rem 2rem;
    transition: right 0.3s ease-in-out;
    gap: 1rem;
    box-shadow: ${props => props.isOpen ? '-5px 0 15px rgba(0,0,0,0.1)' : 'none'};
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem;
  transition: color 0.3s ease;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.secondary};
    transition: width 0.3s ease;
  }

  &:hover:after,
  &.active:after {
    width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    &:last-child {
      border-bottom: none;
    }
  }
`;

// Optional: Add an overlay for mobile menu
const Overlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 99;
  }
`;

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Close menu when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  // Optional: Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('nav')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      <Nav>
        <NavContainer>
          <MenuButton onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <FaTimes /> : <FaBars />}
          </MenuButton>
          <NavLinks isOpen={isOpen}>
            <NavLink 
              to="/" 
              className={location.pathname === '/' ? 'active' : ''}
              onClick={() => setIsOpen(false)}
            >
              Home
            </NavLink>
            <NavLink 
              to="/about"
              className={location.pathname === '/about' ? 'active' : ''}
              onClick={() => setIsOpen(false)}
            >
              About
            </NavLink>
            <NavLink 
              to="/services"
              className={location.pathname === '/services' ? 'active' : ''}
              onClick={() => setIsOpen(false)}
            >
              Services
            </NavLink>
            <NavLink 
              to="/membership"
              className={location.pathname === '/membership' ? 'active' : ''}
              onClick={() => setIsOpen(false)}
            >
              Membership
            </NavLink>
            <NavLink 
              to="/news"
              className={location.pathname === '/news' ? 'active' : ''}
              onClick={() => setIsOpen(false)}
            >
              News
            </NavLink>
            <NavLink 
              to="/contact"
              className={location.pathname === '/contact' ? 'active' : ''}
              onClick={() => setIsOpen(false)}
            >
              Contact
            </NavLink>
          </NavLinks>
        </NavContainer>
      </Nav>
      <Overlay isOpen={isOpen} onClick={() => setIsOpen(false)} />
    </>
  );
};

export default Navigation;